/**
 * A member's identity colour is *their own chosen hex* (household-doc profile, WS-2's
 * `MEMBER_COLORS` palette) rendered inline — never a `bg-member-*` design token. Tokens
 * encode a fixed four-slot palette by roster position or by literal name match, and
 * usability testing (2026-08-01) found that scheme actively wrong: two members who
 * happened to land next to each other (by id-sort or by not being named Faye/Dan/Ezry/Kora)
 * rendered nearly identically, and a member's mark changed the moment someone else joined
 * or renamed themselves. See docs/DECISIONS.md, 2026-08-01 ("member colour truth").
 *
 * These two helpers are the only colour *math* this workstream needs: everything else is
 * "use the hex" or "use the existing neutral/unassigned token treatment".
 */

/** White or near-black — whichever reads legibly as text/an icon glyph directly on top of
 * a solid member-colour background (WCAG relative luminance, sRGB). */
export function textOnMemberColor(hex: string): string {
  const { r, g, b } = parseHex(hex);
  const toLinear = (channel: number): number => {
    const v = channel / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return luminance > 0.45 ? '#121212' : '#FBF8F2';
}

/** A member's hex diluted to a low-alpha wash, for tinted chip/strip backgrounds — plain
 * 8-digit hex alpha rather than `color-mix()`, so it looks the same in every theme without
 * needing to know that theme's surface colour. */
export function memberTint(hex: string, alpha = 0.16): string {
  const alphaHex = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0');
  return `${normalizeHex(hex)}${alphaHex}`;
}

function normalizeHex(hex: string): string {
  return hex.startsWith('#') ? hex : `#${hex}`;
}

function parseHex(hex: string): { r: number; g: number; b: number } {
  const clean = normalizeHex(hex).slice(1);
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;
  return {
    r: parseInt(full.slice(0, 2), 16) || 0,
    g: parseInt(full.slice(2, 4), 16) || 0,
    b: parseInt(full.slice(4, 6), 16) || 0,
  };
}
